import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRobotoCondensed } from "@product/scmp-sdk";

import { BaseImage } from "scmp-app/components/common/base-image";
import { EntityLink } from "scmp-app/components/entity-link";

import IconPlayButton from "./play-button.svg";
import { SeriesArticleItemType } from "./type";

export const StyledIconPlayButton = styled(IconPlayButton)`
  position: absolute;
  z-index: 1;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 25.79px;
    block-size: 25.79px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    inline-size: 55px;
    block-size: 55px;
  }

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    inline-size: 79px;
    block-size: 79px;
  }
`;

export const StyledAuthorEntityLink = styled(EntityLink)`
  &:hover {
    text-decoration: underline;
  }
`;
export const StyledEntityLink = styled(EntityLink)``;

export type Props = {
  $position: "left" | "right";
};

export type ImageProps = {
  $isSmall: boolean;
} & Partial<Props>;

export const StyledBaseImage = styled(BaseImage)<ImageProps>`
  position: sticky;
  inset-block-start: 100px;

  ${props => css`
    ${props.theme.breakpoints.up("tablet")} {
      max-inline-size: ${props.$isSmall ? "368" : "432"}px;
    }
  `}

  ${props => css`
    aspect-ratio: ${props.$aspectRatio};
  `}
`;

export const Container = styled.div<Props>`
  display: grid;

  color: #000000;

  ${props => {
    switch (props.$position) {
      case "left":
        return css`
          grid:
            "left content" min-content
            "image image" min-content
            / 40px minmax(0, 1fr);

          ${props.theme.breakpoints.up("tablet")} {
            grid:
              "pad left content image" min-content
              / calc((100% - 504px) / 2 - 20px) 40px minmax(0, 1fr) calc((100% - 504px) / 2);
          }
        `;
      case "right":
        return css`
          grid:
            "content left" min-content
            "image image" min-content
            / minmax(0, 1fr) 40px;

          ${props.theme.breakpoints.up("tablet")} {
            grid:
              "image content left pad" min-content
              / calc((100% - 504px) / 2) minmax(0, 1fr) 40px calc((100% - 504px) / 2 - 20px);
          }
        `;
    }
  }}
`;

export const Pad = styled.div`
  grid-area: pad;
`;

export const NumberCol = styled.div`
  grid-area: left;

  display: flex;
  justify-content: center;
`;

export const NumberBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  inline-size: 40px;
  block-size: 40px;
  margin-block-start: 24px;

  font-weight: 300;
  font-size: 28px;
  font-family: ${fontRobotoCondensed};
  line-height: 40px;

  background-color: #ffca05;
`;

export const LeftLine = styled.div`
  position: absolute;
  z-index: -1;

  inline-size: 1px;
  block-size: 202px;

  background-color: #000000;
`;

export const ImagePane = styled.div<ImageProps>`
  grid-area: image;

  margin-inline: 19px 19px;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: block;

    margin-inline: 0;
  }

  ${props => {
    switch (props.$position) {
      case "left":
        return css`
          ${props.theme.breakpoints.up("tablet")} {
            padding-inline-start: 8px;
          }
        `;
      case "right":
        return css`
          ${props.theme.breakpoints.up("tablet")} {
            padding-inline-end: 8px;
          }
        `;
    }
  }}

  ${props => {
    if (props.$isSmall) {
      switch (props.$position) {
        case "left":
          return css`
            ${props.theme.breakpoints.up("desktop")} {
              padding-inline-end: 56px;
            }
          `;
        case "right":
          return css`
            ${props.theme.breakpoints.up("desktop")} {
              padding-inline-start: 56px;
            }
          `;
      }
    }
  }}
`;

export type ImageWrapperProps = {
  $stickyOffset: number;
};

export const ImageWrapper = styled(StyledEntityLink)<ImageWrapperProps>`
  position: sticky;
  inset-block-start: ${props => props.$stickyOffset + 40}px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Content = styled.div<Props>`
  grid-area: content;

  margin-block-end: 24px;

  ${props => {
    switch (props.$position) {
      case "left":
        return css`
          padding-block: 22px 0;
          padding-inline: 17px 35px;

          ${props.theme.breakpoints.up("tablet")} {
            padding-block: 22px 0;
            padding-inline: 20px 40px;
          }
        `;
      case "right":
        return css`
          padding-block: 22px 0;
          padding-inline: 35px 17px;

          ${props.theme.breakpoints.up("tablet")} {
            padding-block: 22px 0;
            padding-inline: 48px 20px;
          }
        `;
    }
  }}
`;

export const StyledSeriesArticleItemType = styled(SeriesArticleItemType)`
  margin-block-end: 10px;
`;

export const Headline = styled(StyledEntityLink)`
  margin-block-end: 12px;

  font-weight: 700;
  font-size: 24px;
  font-family: ${fontMerriweather};
  line-height: 32px;

  &:hover {
    color: #2c4692;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 32px;
    line-height: 42px;
  }
`;

export const AuthorsWrapper = styled.div`
  margin-block-end: 20px;

  color: #001246;
  font-size: 14px;
  font-family: ${fontMerriweather};
  font-style: italic;
  line-height: 18px;

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 16px;
    line-height: 20px;
  }
`;

export const Authors = styled.span`
  font-weight: 900;
`;

export const Summary = styled(StyledEntityLink)`
  margin-block-end: 12px;

  color: #222222;
  font-size: 16px;
  line-height: 24px;
  a {
    color: #222222;
    border-block-end: 1px solid #1b8ef8;

    word-break: break-word;
    &:hover {
      color: #4585ff;
    }
    ${props => props.theme.breakpoints.up("desktop")} {
      font-size: 18px;
    }
  }
`;

export const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Updated = styled.span`
  color: #a1a1a1;
  font-size: 14px;
  line-height: 16px;
`;
